import { useEffect, useState } from 'react'

import useRedux from '../../../hooks/useRedux'
import { setFilterDetails } from '../../../redux/dataVisualization/action'
import debounce from '../../../utils/debounce'
import {
  isEqual,
  keys,
  length,
  notEqual,
  ternary,
} from '../../../utils/javascript'
import {
  getAddressApi,
  getBuildingDetailsApi,
  getEquipmentDataApi,
  getEquipmentDetailsApi,
  getEquipmentPieChartDataApi,
  getTotalBrandDataApi,
} from '../visualization.api'
import {
  equipmentInitial,
  getCurrentAddressType,
} from '../visualization.description'

const equipmentView = () => {
  const [formData, setFormData] = useState(equipmentInitial)
  const lastColumn = keys(formData)?.[length(keys(formData)) - 1]
  const [equipmentData, setEquipmentData] = useState({})
  const [brandTotal, setBrandTotal] = useState({})
  const [equipmentLoader, setEquipmentLoader] = useState(false)
  const [buildingEquipmentDetails, setBuildingEquipmentDetails] = useState({
    buildingInfo: { loading: false },
    equipmentInfo: { loading: false },
  })
  const [currentSelectedType, setCurrentSelectedType] = useState('')
  const [pieChartData, setPieChartData] = useState({})
  const [loader, setLoader] = useState({})
  const { selector, dispatch } = useRedux()
  const { tabDetails, filterDetails } = selector(
    state => state?.dataVisualization,
  )
  const { currentTab } = tabDetails
  const { tag: filterTag, value: filterValue, isClear } = filterDetails

  useEffect(() => {
    if (notEqual(currentTab, 'mapView')) {
      getData({
        currentType: 'txt_Country',
        nextTypeTag: 'txt_Country',
      })
      getEquipmentDataList({})
      getPieChartDataList({})
      getBrandTotalAPI({
        params: {
          type: 'CONTINENT',
          visualizationType: ternary(
            notEqual(currentTab, 'mapView'),
            'LIST',
            'MAP',
          ),
        },
      })
    }
  }, [currentTab])

  useEffect(() => {
    if (filterValue || isClear) {
      handleClear({
        currentType: 'txt_Country',
        index: 0,
      })
      if (isClear) {
        dispatch(setFilterDetails({ tag: null, value: null, isClear: false }))
      }
    }
  }, [filterValue])

  useEffect(() => {
    const handleScroll = debounce(e => {
      const container = e.target
      const scrollPosition =
        container.scrollHeight - container.scrollTop - container.clientHeight

      if (scrollPosition <= 2 && equipmentData?.hasMore) {
        const { lastSelected, currentType, pageNo } = { ...equipmentData }
        getEquipmentDataList({
          selected: lastSelected,
          currentType,
          pageNo: pageNo + 1,
          notStateCityList: true,
        })
      }
    }, 500)
    const container = document.querySelector('#equipment-table .ant-table-body')
    container?.addEventListener('scroll', handleScroll)

    return () => {
      container?.removeEventListener('scroll', handleScroll)
    }
  }, [equipmentData])

  const getPayload = ({
    nextType,
    selected,
    type,
    boundary,
    notStateCityList,
  }) => {
    const params = {
      type: getCurrentAddressType[type] || 'CONTINENT',
      visualizationType: ternary(
        notEqual(currentTab, 'mapView'),
        'LIST',
        'MAP',
      ),
      ...(boundary && boundary),
    }

    if (
      (isEqual(currentTab, 'mapView') && notStateCityList) ||
      notEqual(currentTab, 'mapView')
    ) {
      const getLocationValue = (locationType, currentType) => {
        if (isEqual(currentTab, 'mapView') && notStateCityList) {
          return formData[currentType]?.selected?.id || 0
        }
        return isEqual(nextType, locationType)
          ? selected?.id
          : formData[currentType]?.selected?.id || 0
      }

      params.country =
        isEqual(currentTab, 'mapView') && notStateCityList
          ? formData['user_State']?.selected?.country ||
            formData['txt_Country']?.selected?.country ||
            null
          : isEqual(nextType, 'user_State')
            ? selected?.name
            : formData['txt_Country']?.selected?.name || 0
      params.stateId = getLocationValue('user_City', 'user_State')
      params.cityId = getLocationValue('user_Pincode', 'user_City')
      params.pincodeId = getLocationValue('user_Building', 'user_Pincode')
      params.buildingId = getLocationValue('txt_Equipment', 'user_Building')
      params.equipmentId = isEqual(type, 'txt_Equipment')
        ? selected?.id
        : formData['txt_Equipment']?.selected?.id || 0

      if (filterValue) {
        params.filterTag = filterTag?.toUpperCase()
        params.filterValue = filterValue
      }
    }
    return params
  }

  const getEquipmentDataList = async ({
    nextType,
    selected,
    currentType,
    pageNo = 1,
    notStateCityList,
  }) => {
    setEquipmentLoader(true)
    const equipmentParam = getPayload({
      nextType,
      selected,
      type: currentType,
      notStateCityList,
    })
    const equipmentRes = await getEquipmentDataApi({
      params: equipmentParam,
      pageNo,
    })
    if (equipmentRes?.data) {
      const { list, ...rest } = equipmentRes?.data
      setEquipmentData(prev => ({
        ...prev,
        list: length(prev?.list) ? [...prev?.list, ...list] : list,
        lastSelected: selected,
        currentType,
        ...rest,
      }))
    }
    setEquipmentLoader(false)
  }

  const getPieChartDataList = async ({
    nextType,
    selected,
    currentType,
    notStateCityList,
  }) => {
    const pieChartParam = getPayload({
      nextType,
      selected,
      type: currentType,
      notStateCityList,
    })
    const pieChartRes = await getEquipmentPieChartDataApi({
      params: pieChartParam,
    })
    if (pieChartRes?.data) {
      setPieChartData(pieChartRes?.data)
    }
  }

  const getBrandTotalAPI = async ({ params }) => {
    setBrandTotal({})
    const response = await getTotalBrandDataApi({ params })
    if (response?.data) setBrandTotal(response?.data)
  }

  const getData = async ({
    currentType,
    selected,
    nextTypeTag,
    boundary,
    notStateCityList,
    updatedForm,
  }) => {
    const cloneFormData = updatedForm || formData
    const findIndex = keys(cloneFormData)?.findIndex(v =>
      isEqual(v, currentType),
    )
    const nextType =
      nextTypeTag ||
      keys(cloneFormData)?.[
        findIndex + ternary(notEqual(currentTab, 'mapView'), 1, 0)
      ]

    let tempForm = { ...cloneFormData }
    if (selected) {
      tempForm[currentType].selected = selected
    }

    if (notEqual(currentType, lastColumn)) {
      const params = getPayload({
        nextType,
        selected,
        type: nextType,
        boundary,
        notStateCityList,
      })
      setLoader(preLoader => ({ ...preLoader, [nextType]: true }))
      const response = await getAddressApi({ params })
      setLoader(preLoader => ({ ...preLoader, [nextType]: false }))

      setData({
        tempForm,
        response: { list: response?.data?.list },
        findIndex,
        nextTypeTag,
      })
    }

    if (selected) {
      if (tempForm[currentType].selected && notEqual(currentTab, 'mapView')) {
        getEquipmentDataList({ nextType, selected, currentType })
        getPieChartDataList({ nextType, selected, currentType })
        const equipmentParam = getPayload({
          nextType,
          selected,
          type: currentType,
          notStateCityList,
        })
        getBrandTotalAPI({ params: equipmentParam })
      }
    }
    setFormData(tempForm)
  }

  const setData = ({ tempForm, response, findIndex, nextTypeTag }) => {
    Object.entries(formData)?.forEach(([key, value], index) => {
      if (isEqual(nextTypeTag, 'txt_Country')) {
        tempForm['txt_Country'] = {
          selected: null,
          search: null,
          list: response?.list || [],
          filtered: response?.list || [],
        }
      } else if (
        ternary(
          notEqual(currentTab, 'mapView'),
          isEqual(index, findIndex + 1),
          isEqual(index, findIndex),
        )
      ) {
        tempForm[key] = {
          selected: null,
          search: null,
          list: response?.list || [],
          filtered: response?.list || [],
        }
      } else if (index > findIndex + 1) {
        tempForm[key] = {
          selected: null,
          search: null,
          list: [],
          filtered: [],
        }
      }
    })
  }
  const buildingInfoAPI = async ({ buildingId }) => {
    setBuildingEquipmentDetails(prev => ({
      ...prev,
      buildingInfo: { loading: true },
    }))
    const response = await getBuildingDetailsApi({ buildingId })
    if (response?.data) {
      setBuildingEquipmentDetails(prev => ({
        ...prev,
        buildingInfo: { loading: false, ...response?.data },
        equipmentInfo: {},
      }))
    }
  }
  const equipmentInfoAPI = async ({ buildingId, equipmentId }) => {
    setBuildingEquipmentDetails(prev => ({
      ...prev,
      equipmentInfo: { loading: true },
    }))
    const response = await getEquipmentDetailsApi({
      buildingId,
      equipmentId,
    })
    if (response?.data) {
      setBuildingEquipmentDetails(prev => ({
        ...prev,
        equipmentInfo: { loading: false, ...response?.data },
      }))
    }
  }

  const handleSelect = async ({ value, name, isClear }) => {
    setCurrentSelectedType(name)
    const checkField = isEqual(name, 'txt_Country') ? 'name' : 'id'
    if (
      notEqual(formData?.[name]?.selected?.[checkField], value?.[checkField]) ||
      isClear
    ) {
      setEquipmentData({})
      setPieChartData({})
      setBrandTotal({})
      getData({ currentType: name, selected: value })
      if (isEqual(name, 'user_Building')) {
        buildingInfoAPI({ buildingId: value?.id })
      }
      if (isEqual(name, 'txt_Equipment')) {
        equipmentInfoAPI({
          buildingId: formData?.['user_Building']?.selected?.id,
          equipmentId: value?.id,
        })
      }
    }
  }

  const handleClear = async ({ currentType, index, previousType }) => {
    setEquipmentData({})
    setPieChartData({})
    let tempForm = { ...formData }
    tempForm[currentType].selected = null
    setData({ tempForm, findIndex: index })
    setFormData(tempForm)

    if (previousType) {
      handleSelect({
        value: tempForm?.[previousType]?.selected,
        name: previousType,
        isClear: true,
      })
    }

    if (isEqual(currentType, 'txt_Country')) {
      getData({
        currentType: 'txt_Country',
        nextTypeTag: 'txt_Country',
        updatedForm: tempForm,
      })
      getEquipmentDataList({})
      getPieChartDataList({})
    }
  }

  const handleSearch = ({ e, name }) => {
    const { value } = e.target
    setFormData(preFormData => {
      const tempForm = { ...preFormData }
      tempForm[name].search = value
      const list = length(tempForm[name]?.list)
        ? tempForm[name]?.list.filter(v =>
            v?.name.toLowerCase().includes(value.toLowerCase()),
          )
        : []
      tempForm[name].filtered = list
      return tempForm
    })
  }

  return {
    equipmentLoader,
    loader,
    formData,
    equipmentData,
    pieChartData,
    currentTab,
    buildingEquipmentDetails,
    currentSelectedType,
    brandTotal,
    setFormData,
    getData,
    handleSelect,
    handleClear,
    handleSearch,
    getEquipmentDataList,
    getPieChartDataList,
    setEquipmentData,
    setPieChartData,
    buildingInfoAPI,
    equipmentInfoAPI,
    getPayload,
    getBrandTotalAPI,
  }
}

export default equipmentView
